import { GROUP_COLUMN_PREFIX } from 'common/visualizations/views/agGridReact/Constants';
import { getCellAlignment } from 'common/DataTypeFormatter';
import { sortBy, uniqBy, uniq, get, compact } from 'lodash';
import {
  type TableExportType,
  type ExportData,
  type TableColumnFormat,
} from 'common/visualizations/views/agGridReact/types';
import { type GridApi } from 'ag-grid-community';
import { type OrderConfig } from 'common/visualizations/vif';
import {
  type ExtraOptions,
  type GridDataProcessor,
  type GridDataProcessorResult,
  type TableColumn,
} from '../types';
import { processConditionalCellStyles } from './processConditionalCellStyles';
import { buildTotalsRow } from './gridDataAggregator';
import { fetchTableActionsData } from 'common/visualizations/views/agGridReact/customHooks/useTableActionsResults';
import { formatRows } from './gridDataFormatter';
import { groupRows } from './gridDataGrouper';
import { isPivotInGridApi } from 'common/visualizations/helpers/PivotHelpers';
import { parseRows } from './gridDataParser';
import { pivotFromRawDataProcessor, finalizePivotedRows } from './pivot/pivotFromRawData';
import { processPivotFromGridState } from './pivot/pivotFromGridState';
import { replaceWithServerTotals } from './gridDataTotalsReplacer';
import { type NormalRow } from './type';
import {
  isTableAdvancedTotalingForExportsEnabled,
  isTablePrintingImprovementGroupExpansionEnabled,
  isTablePrintingImprovementInheritGroupExpansionEnabled,
} from 'common/visualizations/helpers/VifSelectors';
import { filterRowsForExpansion } from './groupExpansionFilter';

// Note: Retrieving and grouping/formatting data can also be moved to the worker for efficiency.
// Currently, we retrieve 10K records, format/group them in main thread.
// Serialize and send it to worker thread. It again deserializes it (JSON) which is a waste.
// export class GridDataProcessor implements IGridDataProcessor {
export class AgGridDataProcessor implements GridDataProcessor {
  async processGridData(
    gridApi: GridApi,
    getExportData: (selectedFiltered: boolean, exportType: TableExportType) => Promise<ExportData>,
    extraOptions: ExtraOptions,
  ): Promise<GridDataProcessorResult> {
    const exportData = await getExportData(true, 'PDF');
    const tableActionsResult = await getTableActionsResult(extraOptions, exportData.rows as NormalRow[]);

    if (isPivotInGridApi(gridApi)) {
      if (isTablePrintingImprovementGroupExpansionEnabled()) {
        const { pivotedRows } = pivotFromRawDataProcessor(extraOptions, exportData, gridApi);
        // Process the pivot by evaluating all fetched rows and structuring the table in pivot mode.
        return finalizePivotedRows(gridApi, exportData, extraOptions, pivotedRows, tableActionsResult);
      }

      // Process pivot entirely using gridApi from Grid.tsx
      return processPivotFromGridState(gridApi, exportData, extraOptions, tableActionsResult);
    }

    const tableColumns = this.buildTableColumns(gridApi, exportData, extraOptions);
    const orderConfigs = uniqBy(
      extraOptions.vifOrderConfig.map((orderConfig: OrderConfig) => ({
        ...orderConfig,
        columnName: orderConfig.columnName.replace(`${GROUP_COLUMN_PREFIX}-`, ''),
      })),
      'columnName',
    );
    const parsedRows = parseRows(exportData.rows as NormalRow[], tableColumns);
    // Fields whose per-group uniform value the grouper should resolve onto header
    // rows, so grouped parent rows can resolve a table-action link in the PDF.
    const actionColumnFields =
      extraOptions.linkedAction?.selectedExistingColumn && extraOptions.tableActionsContext?.mappedColumns
        ? extraOptions.tableActionsContext.mappedColumns.map((mappedColumn) => mappedColumn.selectedColumn)
        : [];
    const hideDuplicateGroups =
      extraOptions.hideDuplicateGroups &&
      extraOptions.featureFlags?.isTablePrintingImprovementHideDuplicateGroupingsEnabled;
    const groupedRows = groupRows(
      parsedRows,
      tableColumns,
      orderConfigs,
      extraOptions.showSubTotal,
      extraOptions.columnSortConfig,
      actionColumnFields,
      hideDuplicateGroups,
    );

    const rowData = formatRows({
      // conditional styling is evaluated here, on the unformatted rows, so number/currency/percent
      // values are still raw numbers the conditional evaluator can parse. formatRows preserves the
      // attached _conditionalStyles through its row spread.
      rowData: processConditionalCellStyles(
        this.buildRowData(groupedRows, tableColumns, exportData, extraOptions),
        tableColumns,
        extraOptions.columnFormats,
        exportData.columns,
        extraOptions.showSubTotal,
        extraOptions.groupExpansion,
      ),
      tableColumns,
      viewColumns: exportData.columns,
      extraOptions,
      gridApi,
    });

    return {
      rowData,
      tableColumns,
      tableActionsResult,
      tableDef: {
        rowHeight: Number(gridApi.getGridOption('rowHeight')) || 10,
      },
      processedRowCount: exportData.rows.length,
      forgeThemeColorConfig: getForgeThemeColorConfig(),
    };
  }

  private buildTableColumns(
    gridApi: GridApi,
    exportData: ExportData,
    extraOptions: ExtraOptions,
  ): TableColumn[] {
    const groupedFields = gridApi.getRowGroupColumns().map((col) => col.getColId());
    const nonGroupedFields = gridApi
      .getAllDisplayedColumns()
      .map((col) => col.getColId())
      .filter((id) => !id.startsWith(GROUP_COLUMN_PREFIX));
    const visibleColumnIds = [...groupedFields, ...nonGroupedFields];

    return sortBy(exportData.columns, (col) => visibleColumnIds.indexOf(col.fieldName))
      .filter((col) => visibleColumnIds.includes(col.fieldName))
      .map((col) => {
        const gridCol = gridApi.getColumn(col.fieldName);
        const colDef = gridCol?.getColDef();
        const columnFormat: TableColumnFormat | undefined = extraOptions.columnFormats[col.fieldName];
        const textAlign = getCellAlignment(col);
        const align = columnFormat?.format?.align;
        const isAlign = columnFormat?.isFormatValue ?? true;
        return {
          field: col.fieldName,
          displayName: columnFormat?.displayName || col.name || col.fieldName,
          type: col.dataTypeName,
          aggFunc: colDef?.aggFunc?.toString(),
          width: gridCol?.getActualWidth(),
          isGrouped: groupedFields.includes(col.fieldName),
          align: isAlign ? align || textAlign : textAlign,
        };
      });
  }

  private buildRowData(
    groupedRows: NormalRow[],
    tableColumns: TableColumn[],
    exportData: ExportData,
    extraOptions: ExtraOptions,
  ): NormalRow[] {
    let rows = groupedRows;
    const hasServerTotals = this.hasServerTotals(exportData);

    if (hasServerTotals) {
      rows = replaceWithServerTotals(
        rows,
        tableColumns,
        exportData.grandTotals,
        exportData.subtotalLookup,
        exportData.levelMetadata,
      );
    }

    // Apply group expansion filtering after server totals are applied
    if (extraOptions.groupExpansion === 'collapsed') {
      rows = this.filterTopLevelGroupsRaw(rows);
    } else if (
      isTablePrintingImprovementInheritGroupExpansionEnabled() &&
      extraOptions.groupExpansion === 'inherit'
    ) {
      // Filter based on actual grid expansion state
      rows = filterRowsForExpansion(rows, extraOptions.expandedGroupsMap, extraOptions);
    }

    if (extraOptions.showTotal) {
      const totalRow = this.buildTotalRow(hasServerTotals, exportData, rows, tableColumns);
      return [...rows, totalRow];
    }
    return rows;
  }

  private buildTotalRow(
    hasServerTotals: boolean,
    exportData: ExportData,
    rows: NormalRow[],
    tableColumns: TableColumn[],
  ): NormalRow {
    if (hasServerTotals && exportData.grandTotals) {
      // TODO EN-92427 [export-pivot-return-type]: Pivot tables use processPivot() (line 40) and bypass this code,
      // so grandTotals should always be an object here. We handle arrays defensively for type safety.
      const grandTotalsObj = Array.isArray(exportData.grandTotals)
        ? exportData.grandTotals[0]
        : exportData.grandTotals;
      return { ...grandTotalsObj, _rowType: 'total' as const };
    }
    return buildTotalsRow(rows, tableColumns);
  }

  private hasServerTotals(exportData: ExportData): boolean {
    return (
      isTableAdvancedTotalingForExportsEnabled() &&
      ((exportData.subtotalLookup && exportData.subtotalLookup.size > 0) || !!exportData.grandTotals)
    );
  }

  private filterTopLevelGroupsRaw(rows: NormalRow[]): NormalRow[] {
    return rows.filter((row) => row._rowType === 'header' && row._groupLevel === 0);
  }
}

const getTableActionsResult = async (extraOptions: ExtraOptions, rows: NormalRow[]) => {
  const { mappedColumns } = extraOptions.tableActionsContext!;
  const selectedColumn = extraOptions?.linkedAction?.selectedExistingColumn;

  if (!mappedColumns || mappedColumns.length === 0 || !selectedColumn) {
    console.log('No mapped columns or linked action provided for table actions');
    return {
      applicationDescriptors: [],
      showHyperLink: false,
      linkedAction: extraOptions.linkedAction,
      mappedColumns: null,
    };
  }

  // Build mappedColumns with values from the first row for each mapped column
  const mappedColumnsWithValue = mappedColumns.map((contextField) => {
    // Get unique values for this column across all rows
    const values = compact(uniq(rows.map((row) => row[contextField.selectedColumn])));
    return {
      ...contextField,
      value: values[0], // Use first value as representative for the initial fetch
    };
  });

  const discoveryResponse = await fetchTableActionsData({
    contextParams: { ...extraOptions.tableActionsContext, mappedColumns: mappedColumnsWithValue },
  });

  return {
    applicationDescriptors: get(discoveryResponse, '0.applicationDescriptors', []),
    showHyperLink: true,
    linkedAction: extraOptions.linkedAction,
    mappedColumns: mappedColumnsWithValue,
  };
};

export const getForgeThemeColorConfig = () => {
  return {
    mdcThemeBackground: getColor('--forge-theme-surface-dim', COLOR_FALLBACK.surface),
    mdcThemeOnSecondary: getColor('--forge-theme-primary', COLOR_FALLBACK.primary),
    forgeThemeBorderColor: getColor('--forge-theme-border-color', COLOR_FALLBACK.border),
    mdcThemeOnPrimary: getColor('--forge-theme-on-primary', COLOR_FALLBACK.onPrimary),
    pdfPreviewHeaderBackground: getColor(
      '--pdf-preview-header-background',
      COLOR_FALLBACK.pdfPreviewHeaderBackground,
    ),
  };
};

const COLOR_FALLBACK = {
  border: '#000000',
  surface: '#ffffff',
  onPrimary: '#ffffff',
  primary: '#4936b9',
  pdfPreviewHeaderBackground: '#f0f5f9',
};

const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGB_COLOR = /^rgba?\(([^)]+)\)$/i;
const RGB_CHANNEL_SEPARATOR = /[,\s/]+/;

// clamp a 0-255 channel to a 2-digit hex byte, e.g. 224 -> "e0", 5 -> "05", 300 -> "ff"
const toHexByte = (channel: number): string =>
  Math.max(0, Math.min(255, Math.round(channel)))
    .toString(16)
    .padStart(2, '0');

// drop the alpha nibbles so jspdf receives an opaque #rgb / #rrggbb
const stripHexAlpha = (hex: string): string => {
  if (hex.length === 5) {
    return hex.slice(0, 4); // #rgba -> #rgb
  }
  if (hex.length === 9) {
    return hex.slice(0, 7); // #rrggbbaa -> #rrggbb
  }
  return hex;
};

// parse an rgb()/rgba() string into an opaque #rrggbb, or null when it can't be read
const rgbToHex = (value: string): string | null => {
  const match = value.match(RGB_COLOR);
  if (!match) {
    return null;
  }
  const [r, g, b] = match[1].split(RGB_CHANNEL_SEPARATOR).filter(Boolean).map(Number);
  const hasInvalidChannel = [r, g, b].some((channel) => channel === undefined || Number.isNaN(channel));
  if (hasInvalidChannel) {
    return null;
  }
  return `#${toHexByte(r)}${toHexByte(g)}${toHexByte(b)}`;
};

// windows-edge and acrobat reader use the adobe pdf engine, which aborts the table content
// stream on a malformed stroke/fill color. getPropertyValue returns '' for an unresolved var
// (it does not apply the scss var(--x, fallback)), so sanitize to a valid #rrggbb or fall back.
// alpha is dropped since the pdf styles only take opaque colors.
export const sanitizeCssColor = (raw: string, fallback: string): string => {
  const value = raw.trim();
  if (!value) {
    return fallback;
  }
  if (HEX_COLOR.test(value)) {
    return stripHexAlpha(value);
  }
  return rgbToHex(value) ?? fallback;
};

// css variable values are stable within a session, so cache the sanitized result per
// (raw, fallback) pair to avoid re-parsing the same color on each pdf generation.
const sanitizedColorCache = new Map<string, string>();
const memoizedSanitizeCssColor = (raw: string, fallback: string): string => {
  const key = `${raw}|${fallback}`;
  const cached = sanitizedColorCache.get(key);
  if (cached !== undefined) {
    return cached;
  }
  const sanitized = sanitizeCssColor(raw, fallback);
  sanitizedColorCache.set(key, sanitized);
  return sanitized;
};

const getColor = (variableName: string, fallback: string): string =>
  memoizedSanitizeCssColor(getCSSVariableValue(variableName), fallback);

const getCSSVariableValue = (variableName: string): string => {
  const style = window.getComputedStyle(document.body);
  return style.getPropertyValue(variableName);
};
